import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { verifyToken } from '../../services/authService';

const OTP_EXPIRY_TIME = 300; // 5 minutes (in seconds)

const CustomerStampCollect = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState('checking');
  const [error, setError] = useState('');
  const [otpTimer, setOtpTimer] = useState(OTP_EXPIRY_TIME);
  const [restaurant, setRestaurant] = useState(null);
  const [stampCount, setStampCount] = useState(0);
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem('customerToken'));
  const [otpInterval, setOtpInterval] = useState(null);

  // Add processing states
  const [isProcessingPhone, setIsProcessingPhone] = useState(false);
  const [isProcessingVerification, setIsProcessingVerification] =
    useState(false);
  const [isProcessingResendOtp, setIsProcessingResendOtp] = useState(false);
  const [isProcessingSubscribe, setIsProcessingSubscribe] = useState(false);
  const [isProcessingStamp, setIsProcessingStamp] = useState(false);

  useEffect(() => {
    const checkAuthAndSubscription = async () => {
      if (token) {
        try {
          const response = await verifyToken(token);
          if (response.valid) {
            const isSubscribed = await checkSubscription();
            if (isSubscribed) {
              setStep('stamp');
            } else {
              setStep('subscribe');
            }
          } else {
            setStep('phone');
          }
        } catch (error) {
          setStep('phone');
        }
      } else {
        setStep('phone');
      }
    };

    const fetchRestaurantDetails = async () => {
      try {
        const response = await axios.get(`/api/restaurants/${restaurantId}`);
        setRestaurant(response.data);
      } catch (error) {
        setError('Failed to load restaurant information.');
      }
    };

    checkAuthAndSubscription();
    fetchRestaurantDetails();
  }, [token, restaurantId]);

  const checkSubscription = async () => {
    try {
      const response = await axios.get(
        `/api/customers/subscription/${restaurantId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data.isSubscribed;
    } catch (error) {
      return false;
    }
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsProcessingPhone(true); // Show processing state
    try {
      await axios.post('/api/auth/customer/login', { phoneNumber });
      setStep('verify');
      startOtpTimer();
    } catch (error) {
      setError('Failed to send verification code. Please try again.');
    } finally {
      setIsProcessingPhone(false); // Hide processing state
    }
  };

  const startOtpTimer = () => {
    if (otpInterval) clearInterval(otpInterval);

    setOtpTimer(OTP_EXPIRY_TIME); // Reset the timer
    const interval = setInterval(() => {
      setOtpTimer((prev) => {
        if (prev === 1) {
          clearInterval(interval);
        }
        return prev - 1;
      });
    }, 1000);
    setOtpInterval(interval);
  };

  const handleResendOtp = async () => {
    setError('');
    setIsProcessingResendOtp(true); // Show processing state for resend OTP
    try {
      await axios.post('/api/auth/customer/login', { phoneNumber });
      startOtpTimer(); // Reset OTP timer on resend
    } catch (error) {
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setIsProcessingResendOtp(false); // Hide processing state
    }
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsProcessingVerification(true); // Show processing state for verification
    try {
      const response = await axios.post(`/api/auth/customer/verify-otp`, {
        phoneNumber,
        verificationCode,
      });
      if (response.data.token) {
        localStorage.setItem('customerToken', response.data.token);
        setToken(response.data.token);
        const isSubscribed = await checkSubscription();
        setStep(isSubscribed ? 'stamp' : 'subscribe');
      }
    } catch (error) {
      setError('Invalid verification code. Please try again.');
    } finally {
      setIsProcessingVerification(false); // Hide processing state
    }
  };

  const handleGoBack = () => {
    setStep('phone'); // Reset to the phone input step
    setVerificationCode('');
    setOtpTimer(OTP_EXPIRY_TIME); // Reset timer
    clearInterval(otpInterval); // Clear timer when going back
  };

  const handleSubscribe = async () => {
    setError('');
    setIsProcessingSubscribe(true); // Show processing state for subscription
    try {
      const response = await axios.post(
        `/api/customers/subscribe/${restaurantId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setStep('stamp');
      } else {
        setError('Subscription failed. Please try again.');
      }
    } catch (error) {
      setError('Failed to subscribe. Please try again.');
    } finally {
      setIsProcessingSubscribe(false); // Hide processing state
    }
  };

  const handleCollectStamp = async () => {
    setError('');
    setIsProcessingStamp(true); // Show processing state for stamp collection
    try {
      const response = await axios.post(
        `/api/customers/stamps/${restaurantId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setStampCount(response.data.newStampCount);
        setStep('success');
      } else {
        setError(
          response.data.message || 'Failed to collect stamp. Please try again.'
        );
      }
    } catch (error) {
      if (error.response && error.response.data.message) {
        setError(error.response.data.message);
      } else {
        setError('Failed to collect stamp. Please try again.');
      }
    } finally {
      setIsProcessingStamp(false); // Hide processing state
    }
  };

  const renderStep = () => {
    switch (step) {
      case 'checking':
        return <div>Checking authentication...</div>;
      case 'phone':
        return (
          <form onSubmit={handlePhoneSubmit} className='space-y-4'>
            <div>
              <label
                htmlFor='phoneNumber'
                className='block text-sm font-medium text-gray-700'
              >
                Phone Number
              </label>
              <input
                type='tel'
                id='phoneNumber'
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                required
                className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50'
                placeholder='Enter your phone number'
              />
            </div>
            {error && <p className='text-red-500 text-sm'>{error}</p>}
            <button
              type='submit'
              className='w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
              disabled={isProcessingPhone}
            >
              {isProcessingPhone ? 'Processing...' : 'Send Verification Code'}
            </button>
          </form>
        );
      case 'verify':
        return (
          <form onSubmit={handleVerificationSubmit} className='space-y-4'>
            <div>
              <label
                htmlFor='verificationCode'
                className='block text-sm font-medium text-gray-700'
              >
                Verification Code
              </label>
              <input
                type='text'
                id='verificationCode'
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                required
                className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50'
                placeholder='Enter verification code'
              />
            </div>
            {error && <p className='text-red-500 text-sm'>{error}</p>}
            <button
              type='submit'
              className='w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
              disabled={isProcessingVerification}
            >
              {isProcessingVerification
                ? 'Processing...'
                : 'Verify and Continue'}
            </button>
            <button
              onClick={handleGoBack}
              className='w-full mt-2 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-500 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400'
            >
              Go Back
            </button>
            <button
              onClick={handleResendOtp}
              className='w-full mt-2 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-500 hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-400'
              disabled={isProcessingResendOtp}
            >
              {isProcessingResendOtp ? 'Processing...' : 'Resend OTP'}
            </button>
            <p>OTP expires in {otpTimer} seconds</p>
          </form>
        );
      case 'subscribe':
        return (
          <div className='text-center'>
            <p className='text-xl font-semibold text-gray-600 mb-4'>
              Please subscribe to get a stamp
            </p>
            {error && <p className='text-red-500 text-sm'>{error}</p>}
            <button
              onClick={handleSubscribe}
              className='w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
              disabled={isProcessingSubscribe}
            >
              {isProcessingSubscribe ? 'Processing...' : 'Subscribe'}
            </button>
          </div>
        );
      case 'stamp':
        return (
          <div className='text-center'>
            <p className='text-xl font-semibold text-green-600 mb-4'>
              Collect your stamp!
            </p>
            {error && <p className='text-red-500 text-sm'>{error}</p>}
            <button
              onClick={handleCollectStamp}
              className='w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
              disabled={isProcessingStamp}
            >
              {isProcessingStamp ? 'Processing...' : 'Collect Stamp'}
            </button>
          </div>
        );
      case 'success':
        return (
          <div className='text-center'>
            <p className='text-xl font-semibold text-green-600 mb-4'>
              Stamp collected successfully!
            </p>
            <p>Your current stamp count: {stampCount}</p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className='max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-xl'>
      {restaurant && (
        <h1 className='text-2xl font-bold mb-4 text-center'>
          {restaurant.name}
        </h1>
      )}
      {renderStep()}
    </div>
  );
};

export default CustomerStampCollect;

// import React, { useState, useEffect } from 'react';
// import { useParams, useNavigate } from 'react-router-dom';
// import axios from 'axios';
// import { verifyToken } from '../../services/authService';

// const OTP_EXPIRY_TIME = 300; // 5 minutes (in seconds)

// const CustomerStampCollect = () => {
//   const [phoneNumber, setPhoneNumber] = useState('');
//   const [verificationCode, setVerificationCode] = useState('');
//   const [step, setStep] = useState('checking');
//   const [error, setError] = useState('');
//   const [otpTimer, setOtpTimer] = useState(OTP_EXPIRY_TIME);
//   const [restaurant, setRestaurant] = useState(null);
//   const [stampCount, setStampCount] = useState(0);
//   const { restaurantId } = useParams();
//   const navigate = useNavigate();
//   const [token, setToken] = useState(localStorage.getItem('customerToken'));
//   const [otpInterval, setOtpInterval] = useState(null);

//   useEffect(() => {
//     const checkAuthAndSubscription = async () => {
//       if (token) {
//         try {
//           const response = await verifyToken(token);
//           if (response.valid) {
//             const isSubscribed = await checkSubscription();
//             if (isSubscribed) {
//               setStep('stamp');
//             } else {
//               setStep('subscribe');
//             }
//           } else {
//             setStep('phone');
//           }
//         } catch (error) {
//           setStep('phone');
//         }
//       } else {
//         setStep('phone');
//       }
//     };

//     const fetchRestaurantDetails = async () => {
//       try {
//         const response = await axios.get(`/api/restaurants/${restaurantId}`);
//         setRestaurant(response.data);
//       } catch (error) {
//         setError('Failed to load restaurant information.');
//       }
//     };

//     checkAuthAndSubscription();
//     fetchRestaurantDetails();
//   }, [token, restaurantId]);

//   const checkSubscription = async () => {
//     try {
//       const response = await axios.get(
//         `/api/customers/subscription/${restaurantId}`,
//         {
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );
//       return response.data.isSubscribed;
//     } catch (error) {
//       return false;
//     }
//   };

//   const handlePhoneSubmit = async (e) => {
//     e.preventDefault();
//     setError('');
//     try {
//       await axios.post('/api/auth/customer/login', { phoneNumber });
//       setStep('verify');
//       startOtpTimer();
//     } catch (error) {
//       setError('Failed to send verification code. Please try again.');
//     }
//   };

//   const startOtpTimer = () => {
//     if (otpInterval) clearInterval(otpInterval);

//     setOtpTimer(OTP_EXPIRY_TIME); // Reset the timer
//     const interval = setInterval(() => {
//       setOtpTimer((prev) => {
//         if (prev === 1) {
//           clearInterval(interval);
//         }
//         return prev - 1;
//       });
//     }, 1000);
//     setOtpInterval(interval);
//   };

//   const handleResendOtp = async () => {
//     setError('');
//     try {
//       await axios.post('/api/auth/customer/login', { phoneNumber });
//       startOtpTimer(); // Reset OTP timer on resend
//     } catch (error) {
//       setError('Failed to resend OTP. Please try again.');
//     }
//   };

//   const handleVerificationSubmit = async (e) => {
//     e.preventDefault();
//     setError('');
//     try {
//       const response = await axios.post(`/api/auth/customer/verify-otp`, {
//         phoneNumber,
//         verificationCode,
//       });
//       if (response.data.token) {
//         localStorage.setItem('customerToken', response.data.token);
//         setToken(response.data.token);
//         const isSubscribed = await checkSubscription();
//         setStep(isSubscribed ? 'stamp' : 'subscribe');
//       }
//     } catch (error) {
//       setError('Invalid verification code. Please try again.');
//     }
//   };

//   const handleGoBack = () => {
//     setStep('phone'); // Reset to the phone input step
//     setVerificationCode('');
//     setOtpTimer(OTP_EXPIRY_TIME); // Reset timer
//     clearInterval(otpInterval); // Clear timer when going back
//   };

//   const handleSubscribe = async () => {
//     try {
//       const response = await axios.post(
//         `/api/customers/subscribe/${restaurantId}`,
//         {},
//         { headers: { Authorization: `Bearer ${token}` } }
//       );
//       if (response.data.success) {
//         setStep('stamp');
//       } else {
//         setError('Subscription failed. Please try again.');
//       }
//     } catch (error) {
//       setError('Failed to subscribe. Please try again.');
//     }
//   };

//   const handleCollectStamp = async () => {
//     setError('');

//     try {
//       const response = await axios.post(
//         `/api/customers/stamps/${restaurantId}`,
//         {},
//         { headers: { Authorization: `Bearer ${token}` } }
//       );
//       if (response.data.success) {
//         setStampCount(response.data.newStampCount);
//         setStep('success');
//       } else {
//         setError(
//           response.data.message || 'Failed to collect stamp. Please try again.'
//         );
//       }
//     } catch (error) {
//       if (error.response && error.response.data.message) {
//         setError(error.response.data.message);
//       } else {
//         setError('Failed to collect stamp. Please try again.');
//       }
//     }
//   };

//   const renderStep = () => {
//     switch (step) {
//       case 'checking':
//         return <div>Checking authentication...</div>;
//       case 'phone':
//         return (
//           <form onSubmit={handlePhoneSubmit} className='space-y-4'>
//             <div>
//               <label
//                 htmlFor='phoneNumber'
//                 className='block text-sm font-medium text-gray-700'
//               >
//                 Phone Number
//               </label>
//               <input
//                 type='tel'
//                 id='phoneNumber'
//                 value={phoneNumber}
//                 onChange={(e) => setPhoneNumber(e.target.value)}
//                 required
//                 className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50'
//                 placeholder='Enter your phone number'
//               />
//             </div>
//             <button
//               type='submit'
//               className='w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
//             >
//               Send Verification Code
//             </button>
//           </form>
//         );
//       case 'verify':
//         return (
//           <form onSubmit={handleVerificationSubmit} className='space-y-4'>
//             <div>
//               <label
//                 htmlFor='verificationCode'
//                 className='block text-sm font-medium text-gray-700'
//               >
//                 Verification Code
//               </label>
//               <input
//                 type='text'
//                 id='verificationCode'
//                 value={verificationCode}
//                 onChange={(e) => setVerificationCode(e.target.value)}
//                 required
//                 className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50'
//                 placeholder='Enter verification code'
//               />
//             </div>
//             <button
//               type='submit'
//               className='w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
//             >
//               Verify and Continue
//             </button>
//             <button
//               onClick={handleGoBack}
//               className='w-full mt-2 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-500 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400'
//             >
//               Go Back
//             </button>
//             <button
//               onClick={handleResendOtp}
//               className='w-full mt-2 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-500 hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-400'
//             >
//               Resend OTP
//             </button>
//             <p>OTP expires in {otpTimer} seconds</p>
//           </form>
//         );
//       case 'subscribe':
//         return (
//           <div className='text-center'>
//             <p className='text-xl font-semibold text-gray-600 mb-4'>
//               Please subscribe to get a stamp
//             </p>
//             <button
//               onClick={handleSubscribe}
//               className='w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
//             >
//               Subscribe
//             </button>
//           </div>
//         );
//       case 'stamp':
//         return (
//           <div className='text-center'>
//             <p className='text-xl font-semibold text-green-600 mb-4'>
//               Collect your stamp!
//             </p>
//             <button
//               onClick={handleCollectStamp}
//               className='w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
//             >
//               Collect Stamp
//             </button>
//           </div>
//         );
//       case 'success':
//         return (
//           <div className='text-center'>
//             <p className='text-xl font-semibold text-green-600 mb-4'>
//               Stamp collected successfully!
//             </p>
//             <p>Your current stamp count: {stampCount}</p>
//           </div>
//         );
//       default:
//         return null;
//     }
//   };

//   return (
//     <div className='max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-xl'>
//       {restaurant && (
//         <h1 className='text-2xl font-bold mb-4 text-center'>
//           {restaurant.name}
//         </h1>
//       )}
//       {error && <p className='text-red-500 text-sm mb-4'>{error}</p>}
//       {renderStep()}
//     </div>
//   );
// };

// export default CustomerStampCollect;

// import React, { useState, useEffect } from 'react';
// import { useParams, useNavigate } from 'react-router-dom';
// import axios from 'axios';
// import { verifyToken } from '../../services/authService';

// const CustomerStampCollect = () => {
//   const [phoneNumber, setPhoneNumber] = useState('');
//   const [verificationCode, setVerificationCode] = useState('');
//   const [step, setStep] = useState('checking');
//   const [error, setError] = useState('');
//   const [restaurant, setRestaurant] = useState(null);
//   const [stampCount, setStampCount] = useState(0);
//   const { restaurantId } = useParams();
//   const navigate = useNavigate();
//   const [token, setToken] = useState(localStorage.getItem('customerToken'));

//   useEffect(() => {
//     const checkAuthAndSubscription = async () => {
//       console.log('Checking auth and subscription...');
//       if (token) {
//         try {
//           const response = await verifyToken(token);
//           console.log('Token verification response:', response);
//           if (response.valid) {
//             const isSubscribed = await checkSubscription();
//             console.log('Is subscribed:', isSubscribed);
//             if (isSubscribed) {
//               setStep('stamp');
//             } else {
//               setStep('subscribe');
//             }
//           } else {
//             setStep('phone');
//           }
//         } catch (error) {
//           console.error('Error during auth check:', error);
//           setStep('phone');
//         }
//       } else {
//         setStep('phone');
//       }
//     };

//     const fetchRestaurantDetails = async () => {
//       try {
//         const response = await axios.get(`/api/restaurants/${restaurantId}`);
//         setRestaurant(response.data);
//       } catch (error) {
//         console.error('Error fetching restaurant:', error);
//         setError('Failed to load restaurant information.');
//       }
//     };

//     checkAuthAndSubscription();
//     fetchRestaurantDetails();
//   }, [token, restaurantId]);

//   const checkSubscription = async () => {
//     try {
//       const response = await axios.get(
//         `/api/customers/subscription/${restaurantId}`,
//         {
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );
//       console.log('Subscription check response:', response.data);
//       return response.data.isSubscribed;
//     } catch (error) {
//       console.error('Error checking subscription:', error);
//       return false;
//     }
//   };

//   const handlePhoneSubmit = async (e) => {
//     e.preventDefault();
//     setError('');
//     try {
//       await axios.post('/api/auth/customer/login', { phoneNumber });
//       setStep('verify');
//     } catch (error) {
//       console.error('Error sending verification code:', error);
//       setError('Failed to send verification code. Please try again.');
//     }
//   };

//   const handleVerificationSubmit = async (e) => {
//     e.preventDefault();
//     setError('');
//     try {
//       const response = await axios.post(`/api/auth/customer/verify-otp`, {
//         phoneNumber,
//         verificationCode,
//       });
//       if (response.data.token) {
//         localStorage.setItem('customerToken', response.data.token);
//         setToken(response.data.token);
//         const isSubscribed = await checkSubscription();
//         setStep(isSubscribed ? 'stamp' : 'subscribe');
//       }
//     } catch (error) {
//       console.error('Error verifying code:', error);
//       setError('Invalid verification code. Please try again.');
//     }
//   };

//   const handleSubscribe = async () => {
//     try {
//       const response = await axios.post(
//         `/api/customers/subscribe/${restaurantId}`,
//         {},
//         { headers: { Authorization: `Bearer ${token}` } }
//       );
//       if (response.data.success) {
//         setStep('stamp');
//       } else {
//         setError('Subscription failed. Please try again.');
//       }
//     } catch (error) {
//       console.error('Error subscribing:', error);
//       setError('Failed to subscribe. Please try again.');
//     }
//   };

//   const handleCollectStamp = async () => {
//     setError('');

//     try {
//       const response = await axios.post(
//         `/api/customers/stamps/${restaurantId}`,
//         {},
//         { headers: { Authorization: `Bearer ${token}` } }
//       );
//       console.log(response);
//       if (response.data.success) {
//         setError(''); // Clear error on success

//         setStampCount(response.data.newStampCount);
//         setStep('success');
//       } else {
//         setError(
//           response.data.message || 'Failed to collect stamp. Please try again.'
//         );
//       }
//     } catch (error) {
//       // Handle specific error messages here
//       if (error.response && error.response.data.message) {
//         setError(error.response.data.message); // Show specific error from the server
//       } else {
//         setError('Failed to collect stamp. Please try again.');
//       }
//     }
//   };

//   const renderStep = () => {
//     switch (step) {
//       case 'checking':
//         return <div>Checking authentication...</div>;
//       case 'phone':
//         return (
//           <form onSubmit={handlePhoneSubmit} className='space-y-4'>
//             <div>
//               <label
//                 htmlFor='phoneNumber'
//                 className='block text-sm font-medium text-gray-700'
//               >
//                 Phone Number
//               </label>
//               <input
//                 type='tel'
//                 id='phoneNumber'
//                 value={phoneNumber}
//                 onChange={(e) => setPhoneNumber(e.target.value)}
//                 required
//                 className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50'
//                 placeholder='Enter your phone number'
//               />
//             </div>
//             <button
//               type='submit'
//               className='w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
//             >
//               Send Verification Code
//             </button>
//           </form>
//         );
//       case 'verify':
//         return (
//           <form onSubmit={handleVerificationSubmit} className='space-y-4'>
//             <div>
//               <label
//                 htmlFor='verificationCode'
//                 className='block text-sm font-medium text-gray-700'
//               >
//                 Verification Code
//               </label>
//               <input
//                 type='text'
//                 id='verificationCode'
//                 value={verificationCode}
//                 onChange={(e) => setVerificationCode(e.target.value)}
//                 required
//                 className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50'
//                 placeholder='Enter verification code'
//               />
//             </div>
//             <button
//               type='submit'
//               className='w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
//             >
//               Verify and Continue
//             </button>
//           </form>
//         );
//       case 'subscribe':
//         return (
//           <div className='text-center'>
//             <p className='text-xl font-semibold text-gray-600 mb-4'>
//               Please subscribe to get a stamp
//             </p>
//             <button
//               onClick={handleSubscribe}
//               className='w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
//             >
//               Subscribe
//             </button>
//           </div>
//         );
//       case 'stamp':
//         return (
//           <div className='text-center'>
//             <p className='text-xl font-semibold text-green-600 mb-4'>
//               Collect your stamp!
//             </p>
//             <button
//               onClick={handleCollectStamp}
//               className='w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
//             >
//               Collect Stamp
//             </button>
//           </div>
//         );
//       case 'success':
//         return (
//           <div className='text-center'>
//             <p className='text-xl font-semibold text-green-600 mb-4'>
//               Stamp collected successfully!
//             </p>
//             <p>Your current stamp count: {stampCount}</p>
//           </div>
//         );
//       default:
//         return null;
//     }
//   };

//   return (
//     <div className='max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-xl'>
//       {restaurant && (
//         <h1 className='text-2xl font-bold mb-4 text-center'>
//           {restaurant.name}
//         </h1>
//       )}
//       {/* <h2 className='text-xl mb-6 text-center'>Collect Your Stamp</h2> */}
//       {error && <p className='text-red-500 text-sm mb-4'>{error}</p>}
//       {renderStep()}
//     </div>
//   );
// };

// export default CustomerStampCollect;
